'use client';

import { useState } from 'react';
import CertificationCard from './CertificationCard';
import AuditTimelineItem from './AuditTimelineItem';
import TrainingModuleCard from './TrainingModuleCard';
import PerformanceMetricCard from './PerformanceMetricCard';
import DocumentLibraryItem from './DocumentLibraryItem';
import ContinuousImprovementCard from './ContinuousImprovementCard';
import Icon from '@/components/ui/AppIcon';

interface Certification {
  id: number;
  title: string;
  issuer: string;
  certNumber: string;
  validUntil: string;
  scope: string;
  image: string;
  alt: string;
  status: 'active' | 'expiring' | 'renewal';
}

interface AuditItem {
  id: number;
  date: string;
  auditType: string;
  auditor: string;
  result: 'passed' | 'minor' | 'major';
  findings: number;
  corrective: number;
}

interface TrainingModule {
  id: number;
  title: string;
  category: string;
  duration: string;
  completionRate: number;
  enrolled: number;
  certified: number;
  lastUpdated: string;
}

interface PerformanceMetric {
  id: number;
  title: string;
  value: string;
  change: number;
  trend: 'up' | 'down' | 'stable';
  icon: string;
  description: string;
}

interface DocumentItem {
  id: number;
  title: string;
  category: string;
  version: string;
  lastModified: string;
  fileSize: string;
  fileType: 'pdf' | 'doc' | 'xls';
  downloads: number;
}

interface ImprovementProject {
  id: number;
  title: string;
  description: string;
  status: 'completed' | 'in-progress' | 'planned';
  impact: string;
  startDate: string;
  completionDate: string;
  team: string;
  image: string;
  alt: string;
}

const mockCertifications: Certification[] = [
{
  id: 1,
  title: "ISO 9001:2015",
  issuer: "International Organization for Standardization",
  certNumber: "ISO-9001-2024-PW-001",
  validUntil: "December 2026",
  scope: "Design, manufacture, and supply of precision metal fabrication components",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f7d9435b-1767388841681.png",
  alt: "ISO 9001 quality management certification document with official seal and stamps",
  status: "active"
},
{
  id: 2,
  title: "ISO 14001:2015",
  issuer: "International Organization for Standardization",
  certNumber: "ISO-14001-2024-PW-002",
  validUntil: "March 2027",
  scope: "Environmental management systems for manufacturing operations",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_16bdd3b5d-1764657833875.png",
  alt: "Environmental management certification with green sustainability badge",
  status: "active"
},
{
  id: 3,
  title: "ISO 45001:2018",
  issuer: "International Organization for Standardization",
  certNumber: "ISO-45001-2025-PW-003",
  validUntil: "June 2026",
  scope: "Occupational health and safety management systems",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17b5b6770-1764691525900.png",
  alt: "Workplace safety certification document with hard hat and safety equipment icons",
  status: "expiring"
},
{
  id: 4,
  title: "AS9100D",
  issuer: "International Aerospace Quality Group",
  certNumber: "AS-9100D-2024-PW-004",
  validUntil: "September 2027",
  scope: "Aerospace quality management systems for precision components",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d16dc59d-1768628392265.png",
  alt: "Aerospace quality certification with aircraft component manufacturing badge",
  status: "active"
}];


const mockAuditTimeline: AuditItem[] = [
{
  id: 1,
  date: "January 15, 2026",
  auditType: "ISO 9001 Surveillance Audit",
  auditor: "TÜV SÜD America Inc.",
  result: "passed",
  findings: 0,
  corrective: 0
},
{
  id: 2,
  date: "November 8, 2025",
  auditType: "AS9100D Recertification Audit",
  auditor: "SAI Global Assurance Services",
  result: "minor",
  findings: 2,
  corrective: 2
},
{
  id: 3,
  date: "August 22, 2025",
  auditType: "ISO 14001 Internal Audit",
  auditor: "Internal Quality Team",
  result: "passed",
  findings: 1,
  corrective: 1
},
{
  id: 4,
  date: "May 10, 2025",
  auditType: "ISO 45001 Surveillance Audit",
  auditor: "Bureau Veritas North America",
  result: "passed",
  findings: 0,
  corrective: 0
}];


const mockTrainingModules: TrainingModule[] = [
{
  id: 1,
  title: "ISO 9001 Quality Management Fundamentals",
  category: "Quality Systems",
  duration: "4 hours",
  completionRate: 94,
  enrolled: 48,
  certified: 45,
  lastUpdated: "Jan 2026"
},
{
  id: 2,
  title: "Root Cause Analysis & Corrective Actions",
  category: "Problem Solving",
  duration: "3 hours",
  completionRate: 87,
  enrolled: 42,
  certified: 37,
  lastUpdated: "Dec 2025"
},
{
  id: 3,
  title: "Internal Audit Procedures",
  category: "Audit Training",
  duration: "6 hours",
  completionRate: 91,
  enrolled: 28,
  certified: 25,
  lastUpdated: "Nov 2025"
},
{
  id: 4,
  title: "Document Control & Management",
  category: "Quality Systems",
  duration: "2 hours",
  completionRate: 96,
  enrolled: 52,
  certified: 50,
  lastUpdated: "Jan 2026"
},
{
  id: 5,
  title: "Statistical Process Control",
  category: "Quality Tools",
  duration: "5 hours",
  completionRate: 82,
  enrolled: 35,
  certified: 29,
  lastUpdated: "Oct 2025"
},
{
  id: 6,
  title: "Environmental Management Systems",
  category: "Environmental",
  duration: "3 hours",
  completionRate: 89,
  enrolled: 40,
  certified: 36,
  lastUpdated: "Dec 2025"
}];


const mockPerformanceMetrics: PerformanceMetric[] = [
{
  id: 1,
  title: "On-Time Delivery",
  value: "98.7%",
  change: 2.3,
  trend: "up",
  icon: "TruckIcon",
  description: "vs. previous quarter"
},
{
  id: 2,
  title: "First Pass Yield",
  value: "96.2%",
  change: 1.8,
  trend: "up",
  icon: "CheckBadgeIcon",
  description: "vs. previous quarter"
},
{
  id: 3,
  title: "Customer Complaints",
  value: "0.3%",
  change: 0.5,
  trend: "down",
  icon: "ExclamationCircleIcon",
  description: "vs. previous quarter"
},
{
  id: 4,
  title: "Capacity Utilization",
  value: "87.5%",
  change: 0,
  trend: "stable",
  icon: "ChartBarIcon",
  description: "vs. previous quarter"
}];


const mockDocuments: DocumentItem[] = [
{
  id: 1,
  title: "Quality Management System Manual",
  category: "Quality Systems",
  version: "5.2",
  lastModified: "Jan 10, 2026",
  fileSize: "2.4 MB",
  fileType: "pdf",
  downloads: 156
},
{
  id: 2,
  title: "Corrective Action Procedure",
  category: "Procedures",
  version: "3.1",
  lastModified: "Dec 15, 2025",
  fileSize: "1.8 MB",
  fileType: "pdf",
  downloads: 203
},
{
  id: 3,
  title: "Internal Audit Schedule 2026",
  category: "Audit Planning",
  version: "1.0",
  lastModified: "Jan 5, 2026",
  fileSize: "856 KB",
  fileType: "xls",
  downloads: 89
},
{
  id: 4,
  title: "Training Effectiveness Report Q4 2025",
  category: "Training",
  version: "2.0",
  lastModified: "Jan 8, 2026",
  fileSize: "1.2 MB",
  fileType: "doc",
  downloads: 67
},
{
  id: 5,
  title: "Environmental Aspects Register",
  category: "Environmental",
  version: "4.3",
  lastModified: "Dec 20, 2025",
  fileSize: "3.1 MB",
  fileType: "xls",
  downloads: 124
},
{
  id: 6,
  title: "Management Review Minutes",
  category: "Management",
  version: "1.0",
  lastModified: "Jan 12, 2026",
  fileSize: "945 KB",
  fileType: "pdf",
  downloads: 78
}];


const mockImprovementProjects: ImprovementProject[] = [
{
  id: 1,
  title: "Automated Quality Inspection System",
  description: "Implementation of AI-powered vision inspection system to reduce manual inspection time and improve defect detection accuracy",
  status: "in-progress",
  impact: "35% reduction in inspection time, 99.2% defect detection rate",
  startDate: "Oct 2025",
  completionDate: "Mar 2026",
  team: "Quality Engineering",
  image: "https://images.unsplash.com/photo-1562045289-8e02d9a3a61f",
  alt: "Robotic arm with camera system performing automated quality inspection on metal parts"
},
{
  id: 2,
  title: "Lean Manufacturing Initiative",
  description: "5S implementation and value stream mapping to eliminate waste and optimize production flow across all manufacturing cells",
  status: "completed",
  impact: "22% increase in throughput, 18% reduction in lead time",
  startDate: "Jan 2025",
  completionDate: "Dec 2025",
  team: "Operations Excellence",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1359a50e8-1765990052708.png",
  alt: "Clean organized manufacturing floor with labeled workstations and visual management boards"
},
{
  id: 3,
  title: "Predictive Maintenance Program",
  description: "IoT sensor deployment and machine learning algorithms to predict equipment failures before they occur, reducing unplanned downtime",
  status: "planned",
  impact: "Projected 40% reduction in unplanned downtime",
  startDate: "Apr 2026",
  completionDate: "Oct 2026",
  team: "Maintenance & Engineering",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_158cca1e2-1767261010943.png",
  alt: "Industrial equipment with IoT sensors and digital monitoring dashboard displaying real-time performance data"
}];


export default function QualityISOInteractive() {
  const [activeTab, setActiveTab] = useState<'certifications' | 'audits' | 'training' | 'metrics' | 'documents' | 'improvements'>('certifications');
  const [searchQuery, setSearchQuery] = useState('');

  const tabs = [
  { id: 'certifications', label: 'Certifications', icon: 'ShieldCheckIcon' },
  { id: 'audits', label: 'Audit Timeline', icon: 'ClipboardDocumentCheckIcon' },
  { id: 'training', label: 'Training', icon: 'AcademicCapIcon' },
  { id: 'metrics', label: 'Performance', icon: 'ChartBarIcon' },
  { id: 'documents', label: 'Documents', icon: 'FolderIcon' },
  { id: 'improvements', label: 'Improvements', icon: 'LightBulbIcon' }];


  return (
    <div className="min-h-screen bg-background">
      <div className="relative bg-gradient-to-br from-primary via-primary/95 to-secondary py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-primary-foreground/20 rounded-lg flex items-center justify-center">
              <Icon name="ShieldCheckIcon" size={28} variant="outline" className="text-primary-foreground" />
            </div>
            <h1 className="text-4xl md:text-5xl font-headline font-bold text-primary-foreground">
              Quality & ISO Hub
            </h1>
          </div>
          <p className="text-lg font-body max-w-3xl pl-[13px] text-[rgba(16,15,15,1)]">
            Comprehensive certification center with audit summaries, training materials, and compliance tracking systems. Your command center for quality excellence and continuous improvement.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-card rounded-lg border border-border p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 mb-6">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Icon name="MagnifyingGlassIcon" size={20} variant="outline" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search certifications, documents, training..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-md text-sm font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />

              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button className="px-4 py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-cta font-bold hover:opacity-90 transition-all duration-base flex items-center space-x-2">
                <Icon name="DocumentArrowDownIcon" size={18} variant="outline" />
                <span>Export Report</span>
              </button>
              <button className="px-4 py-2.5 bg-conversion text-conversion-foreground rounded-md text-sm font-cta font-bold hover:opacity-90 transition-all duration-base flex items-center space-x-2">
                <Icon name="CalendarIcon" size={18} variant="outline" />
                <span>Schedule Audit</span>
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) =>
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2.5 rounded-md text-sm font-cta font-bold transition-all duration-base flex items-center space-x-2 ${
              activeTab === tab.id ?
              'bg-primary text-primary-foreground' :
              'bg-muted text-muted-foreground hover:bg-muted/80'}`
              }>

                <Icon name={tab.icon as any} size={18} variant={activeTab === tab.id ? 'solid' : 'outline'} />
                <span>{tab.label}</span>
              </button>
            )}
          </div>
        </div>

        {activeTab === 'certifications' &&
        <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-headline font-bold text-foreground">Active Certifications</h2>
              <span className="text-sm font-body text-muted-foreground">{mockCertifications.length} certifications</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mockCertifications.map((cert) =>
            <CertificationCard key={cert.id} {...cert} />
            )}
            </div>
          </div>
        }

        {activeTab === 'audits' &&
        <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-headline font-bold text-foreground">Audit History</h2>
              <span className="text-sm font-body text-muted-foreground">{mockAuditTimeline.length} audits completed</span>
            </div>
            <div className="max-w-4xl">
              {mockAuditTimeline.map((audit, index) =>
            <AuditTimelineItem
              key={audit.id}
              {...audit}
              isLast={index === mockAuditTimeline.length - 1} />

            )}
            </div>
          </div>
        }

        {activeTab === 'training' &&
        <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-headline font-bold text-foreground">Training Modules</h2>
              <span className="text-sm font-body text-muted-foreground">{mockTrainingModules.length} active modules</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mockTrainingModules.map((module) =>
            <TrainingModuleCard key={module.id} {...module} />
            )}
            </div>
          </div>
        }

        {activeTab === 'metrics' &&
        <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-headline font-bold text-foreground">Performance Metrics</h2>
              <span className="text-sm font-body text-muted-foreground">Q1 2026 Performance</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mockPerformanceMetrics.map((metric) =>
            <PerformanceMetricCard key={metric.id} {...metric} />
            )}
            </div>
          </div>
        }

        {activeTab === 'documents' &&
        <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-headline font-bold text-foreground">Document Library</h2>
              <span className="text-sm font-body text-muted-foreground">{mockDocuments.length} documents</span>
            </div>
            <div className="space-y-4">
              {mockDocuments.map((doc) =>
            <DocumentLibraryItem key={doc.id} {...doc} />
            )}
            </div>
          </div>
        }

        {activeTab === 'improvements' &&
        <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-headline font-bold text-foreground">Continuous Improvement Projects</h2>
              <span className="text-sm font-body text-muted-foreground">{mockImprovementProjects.length} projects</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mockImprovementProjects.map((project) =>
            <ContinuousImprovementCard key={project.id} {...project} />
            )}
            </div>
          </div>
        }
      </div>

      <div className="bg-card border-t border-border py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="ShieldCheckIcon" size={32} variant="outline" className="text-primary" />
              </div>
              <h3 className="text-xl font-headline font-bold text-foreground mb-2">Certified Excellence</h3>
              <p className="text-sm font-body text-muted-foreground">
                Multiple ISO certifications and industry-specific quality standards maintained through rigorous audit processes
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AcademicCapIcon" size={32} variant="outline" className="text-primary" />
              </div>
              <h3 className="text-xl font-headline font-bold text-foreground mb-2">Continuous Training</h3>
              <p className="text-sm font-body text-muted-foreground">
                Comprehensive training programs ensuring team competency and compliance with quality management systems
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="ChartBarIcon" size={32} variant="outline" className="text-primary" />
              </div>
              <h3 className="text-xl font-headline font-bold text-foreground mb-2">Performance Tracking</h3>
              <p className="text-sm font-body text-muted-foreground">
                Real-time metrics and KPIs monitoring quality performance, delivery excellence, and continuous improvement
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>);

}